import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import Sidebar from "../components/Sidebar";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Separator } from "../components/ui/separator";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import axios from "axios";
import { toast } from "sonner";
import { 
  ArrowLeft, 
  Target, 
  Leaf, 
  Users, 
  Clock,
  Shield,
  CheckCircle,
  Lightbulb,
  Trash2,
  Calendar,
  Download
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DecisionDetail() {
  const { id } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [decision, setDecision] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const fetchDecision = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/decisions/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDecision(response.data);
    } catch (error) {
      toast.error("Décision introuvable");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  }, [id, token, navigate]);

  useEffect(() => {
    fetchDecision();
  }, [fetchDecision]);

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/decisions/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Décision supprimée");
      navigate("/dashboard");
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleExportPdf = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/decisions/${id}/export/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `decision_${id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF téléchargé !");
    } catch (error) {
      toast.error("Erreur lors de l'export PDF");
    } finally {
      setExporting(false);
    }
  };

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/decisions/${id}/export/excel`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `decision_${id}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Excel téléchargé !");
    } catch (error) {
      toast.error("Erreur lors de l'export Excel");
    } finally {
      setExporting(false);
    }
  };

  const getDecisionTypeInfo = (type) => {
    const types = {
      strategique: { 
        label: "Stratégique", 
        color: "bg-chart-1 text-white",
        description: "Décision à long terme engageant l'organisation globalement"
      },
      tactique: { 
        label: "Tactique", 
        color: "bg-chart-3 text-white",
        description: "Décision à moyen terme concernant un département ou projet"
      },
      operationnelle: { 
        label: "Opérationnelle", 
        color: "bg-chart-5 text-white",
        description: "Décision à court terme pour l'exécution quotidienne"
      }
    };
    return types[type] || types.tactique;
  };

  const getScoreColor = (score) => {
    if (score >= 70) return "text-chart-1";
    if (score >= 50) return "text-chart-4";
    return "text-destructive";
  };

  const getScoreBg = (score) => {
    if (score >= 70) return "bg-chart-1";
    if (score >= 50) return "bg-chart-4";
    return "bg-destructive";
  };

  if (loading) {
    return (
      <div className="min-h-screen flex">
        <Sidebar />
        <main className="flex-1 ml-[260px] p-8 bg-background">
          <div className="flex items-center justify-center h-full">
            <div className="animate-pulse-soft text-primary text-lg">Chargement...</div>
          </div>
        </main>
      </div>
    );
  }

  if (!decision) return null;

  const typeInfo = getDecisionTypeInfo(decision.decision_type);

  return (
    <div className="min-h-screen flex">
      <Sidebar />
      <main className="flex-1 ml-[260px] p-8 bg-background" data-testid="decision-detail-page">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <Link 
              to="/dashboard" 
              className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Retour au tableau de bord
            </Link>
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <Badge className={typeInfo.color}>{typeInfo.label}</Badge>
                  <span className="text-sm text-muted-foreground flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {new Date(decision.created_at).toLocaleDateString('fr-FR', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric'
                    })}
                  </span>
                </div>
                <h1 className="text-3xl font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>
                  {decision.title}
                </h1>
                <p className="text-muted-foreground mt-2">{typeInfo.description}</p>
              </div>
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleExportExcel}
                  disabled={exporting}
                  data-testid="export-decision-excel-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  {exporting ? "Export..." : "Excel"}
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleExportPdf}
                  disabled={exporting}
                  data-testid="export-decision-pdf-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  {exporting ? "Export..." : "PDF"}
                </Button>
                <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" size="sm" className="text-destructive hover:text-destructive">
                    <Trash2 className="w-4 h-4 mr-2" />
                    Supprimer
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
                    <AlertDialogDescription>
                      Cette action est irréversible. La décision sera définitivement supprimée.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Annuler</AlertDialogCancel>
                    <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
                      Supprimer
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
              </div>
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Description */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                    <Target className="w-5 h-5 text-primary" />
                    Description
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-foreground">{decision.description}</p>
                  <Separator />
                  <div>
                    <h4 className="font-medium text-foreground mb-2">Contexte</h4>
                    <p className="text-muted-foreground">{decision.context}</p>
                  </div>
                  <div>
                    <h4 className="font-medium text-foreground mb-2">Impact attendu</h4>
                    <p className="text-muted-foreground">{decision.expected_impact}</p>
                  </div>
                </CardContent>
              </Card>

              {/* Stakeholders */}
              {decision.stakeholders?.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                      <Users className="w-5 h-5 text-chart-3" />
                      Parties prenantes
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {decision.stakeholders.map((stakeholder, index) => (
                        <Badge key={index} variant="secondary" className="px-3 py-1">
                          {stakeholder}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* AI Analysis */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                    <Lightbulb className="w-5 h-5 text-chart-4" />
                    Analyse IA
                  </CardTitle>
                  <CardDescription>
                    Analyse générée par GPT-5.2 selon les critères CSRD
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-foreground">{decision.ai_analysis}</p>
                  
                  {decision.ai_recommendations?.length > 0 && (
                    <>
                      <Separator />
                      <div>
                        <h4 className="font-medium text-foreground mb-3">Recommandations</h4>
                        <ul className="space-y-2">
                          {decision.ai_recommendations.map((rec, index) => (
                            <li key={index} className="flex items-start gap-2">
                              <CheckCircle className="w-4 h-4 text-chart-1 mt-0.5 flex-shrink-0" />
                              <span className="text-muted-foreground">{rec}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>

              {/* CSRD Conformity */}
              {decision.conformity_flags?.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                      <Shield className="w-5 h-5 text-primary" />
                      Conformité CSRD/ESRS
                    </CardTitle>
                    <CardDescription>
                      Standards ESRS applicables à cette décision
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {decision.conformity_flags.map((flag, index) => (
                        <Badge key={index} variant="outline" className="px-3 py-1">
                          {flag}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Sidebar - Scores */}
            <div className="space-y-6">
              {/* Overall Score */}
              <Card className="border-2" style={{ borderColor: `hsl(var(--chart-1))` }}>
                <CardContent className="p-6 text-center">
                  <div className="text-sm text-muted-foreground mb-2">Score ESG Global</div>
                  <div className={`text-5xl font-bold ${getScoreColor(decision.overall_sustainability_score)}`} 
                       style={{ fontFamily: 'Manrope' }}
                       data-testid="overall-score">
                    {decision.overall_sustainability_score}%
                  </div>
                  <div className={`text-sm mt-2 ${getScoreColor(decision.overall_sustainability_score)}`}>
                    {decision.overall_sustainability_score >= 70 ? "Excellent" : 
                     decision.overall_sustainability_score >= 50 ? "Acceptable" : "À améliorer"}
                  </div>
                </CardContent>
              </Card>

              {/* ESG Breakdown */}
              <Card>
                <CardHeader>
                  <CardTitle style={{ fontFamily: 'Manrope' }}>Détail ESG</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Environnement */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-chart-1" />
                        <span className="text-sm font-medium">Environnement</span>
                      </div>
                      <span className={`font-bold ${getScoreColor(decision.esg_scores?.environnement || 0)}`}>
                        {decision.esg_scores?.environnement || 0}%
                      </span>
                    </div>
                    <Progress 
                      value={decision.esg_scores?.environnement || 0} 
                      className="h-2"
                    />
                  </div>

                  {/* Social */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-chart-3" />
                        <span className="text-sm font-medium">Social</span>
                      </div>
                      <span className={`font-bold ${getScoreColor(decision.esg_scores?.social || 0)}`}>
                        {decision.esg_scores?.social || 0}%
                      </span>
                    </div>
                    <Progress 
                      value={decision.esg_scores?.social || 0} 
                      className="h-2"
                    />
                  </div>

                  {/* Gouvernance */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-chart-5" />
                        <span className="text-sm font-medium">Gouvernance</span>
                      </div>
                      <span className={`font-bold ${getScoreColor(decision.esg_scores?.gouvernance || 0)}`}>
                        {decision.esg_scores?.gouvernance || 0}%
                      </span>
                    </div>
                    <Progress 
                      value={decision.esg_scores?.gouvernance || 0} 
                      className="h-2"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Timeframe */}
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center">
                      <Clock className="w-5 h-5 text-muted-foreground" />
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground">Horizon temporel</div>
                      <div className="font-medium text-foreground capitalize">{decision.timeframe}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
